import React from "react";
import { UserAvatar } from "@/components/ui/user-avatar";
import { ItineraryStatusBadge } from "./ItineraryStatusBadge";
import { ItineraryActions } from "./ItineraryActions";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "@/components/ui/pagination";
import { cn, formatTravelDates } from "@/components/utils/utils";
import { DashboardItinerary } from "@/lib/models/dashboard";
import { ItineraryStatus } from "@/lib/builder/models/itinerary.model";
import { useI18n } from '@/hooks/useI18n';
import { useModal } from "@/contexts/ModalContext";
import { getStorageThumbnailUrl } from "@/utils/imageStorage";
import { ExternalAgentBadge } from "@/lib/itineraries/ExternalAgentBadge";

interface ItinerariesTableProps {
    itineraries: DashboardItinerary[];
    currentPage: number;
    itemsPerPage: number;
    onPageChange: (page: number) => void;
    onEdit?: (itineraryId: string) => void;
    onDelete?: (itineraryId: string) => void;
    onDuplicate?: (itineraryId: string) => void;
    onViewTravelRequest?: (travelRequestId: string) => void;
    onViewItinerary?: (itineraryId: string) => void;
    onUpdateStatus?: (itineraryId: string, status: ItineraryStatus) => void;
    onShared?: () => void;
    isAdmin?: boolean;
    isTraveler?: boolean;
    userRole?: string;
}

interface ItineraryTableRowProps {
    itinerary: DashboardItinerary;
    isAdmin?: boolean;
    isTraveler?: boolean;
    userRole?: string;
    onEdit?: (itineraryId: string) => void;
    onDelete?: (itineraryId: string) => void;
    onDuplicate?: (itineraryId: string) => void;
    onViewTravelRequest?: (travelRequestId: string) => void;
    onViewItinerary?: (itineraryId: string) => void;
    onUpdateStatus?: (itineraryId: string, status: ItineraryStatus) => void;
    onShared?: () => void;
    onRowClick: (itinerary: DashboardItinerary) => void;
}

const ItineraryTableRow: React.FC<ItineraryTableRowProps> = ({
    itinerary,
    isAdmin,
    isTraveler,
    userRole,
    onEdit,
    onDelete,
    onDuplicate,
    onViewTravelRequest,
    onViewItinerary,
    onUpdateStatus,
    onShared,
    onRowClick,
}) => {
    const { t } = useI18n();

    return (
        <TableRow
            className="cursor-pointer hover:bg-gray-50"
            onClick={() => onRowClick(itinerary)}
        >
            {/* Titolo con thumbnail */}
            <TableCell>
                <div className="flex items-center gap-3">
                    <div
                        className="w-10 h-10 rounded-md bg-gray-200 bg-cover bg-center flex-shrink-0"
                        style={{
                            backgroundImage: itinerary.image
                                ? `url(${getStorageThumbnailUrl(itinerary.image.url, { noTransform: true })})`
                                : undefined,
                        }}
                    />
                    <div className="font-medium text-gray-900">
                        {itinerary.title || t('itinerary.untitledItinerary')}
                    </div>
                </div>
            </TableCell>

            {/* Date */}
            <TableCell className="text-gray-600">
                {formatTravelDates(itinerary.startDate, itinerary.endDate)}
            </TableCell>

            {/* Ospiti */}
            <TableCell className="text-gray-600">
                {itinerary.travelRequest.totalGuests ?? '—'} {itinerary.travelRequest.totalGuests === 1 ? t('common.guest') : t('common.guests')}
            </TableCell>

            {/* Status */}
            <TableCell>
                <div className="flex items-center inline-flex">
                    {/* detailedStatus, non status: a viaggio confermato ('completed') il
                        badge distingue In programma / In corso / Viaggio passato in base
                        alle date, come fa la vista a card. Con lo status grezzo la cella
                        mostrava un badge verde senza testo. */}
                    <ItineraryStatusBadge status={itinerary.detailedStatus} userRole={userRole} />
                </div>
            </TableCell>

            {/* Contatto */}
            <TableCell>
                <div className="flex items-center gap-2">
                    <UserAvatar
                        fullName={itinerary.contactFullName}
                        email={itinerary.contactEmail}
                        imageUrl={itinerary.travelRequest.contact.avatarUrl}
                        size="sm"
                    />
                    <div>
                        <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">
                                {itinerary.contactFullName}
                            </span>
                            {itinerary.isExternalAgent && <ExternalAgentBadge />}
                        </div>
                        <div className="text-sm text-gray-500">{itinerary.contactEmail}</div>
                    </div>
                </div>
            </TableCell>

            {/* Designer (solo admin) */}
            {isAdmin && (
                <TableCell>
                    {itinerary.assignedAdvisor ? (
                        <div className="flex items-center gap-2">
                            <UserAvatar
                                firstName={itinerary.assignedAdvisor.firstName}
                                lastName={itinerary.assignedAdvisor.lastName}
                                email={itinerary.assignedAdvisor.email}
                                imageUrl={itinerary.assignedAdvisor.avatarUrl}
                                size="sm"
                            />
                            <span className="text-gray-900">
                                {itinerary.assignedAdvisor.firstName} {itinerary.assignedAdvisor.lastName}
                            </span>
                        </div>
                    ) : (
                        <span className="text-sm text-gray-400">{t('travelRequest.notAssigned')}</span>
                    )}
                </TableCell>
            )}

            {/* Azioni */}
            <TableCell onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-1">
                    <ItineraryActions
                        itinerary={itinerary}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onDuplicate={onDuplicate}
                        onViewTravelRequest={onViewTravelRequest}
                        onViewItinerary={onViewItinerary}
                        onUpdateStatus={onUpdateStatus}
                        onShared={onShared}
                        isAdmin={isAdmin}
                        isTraveler={isTraveler}
                        userRole={userRole}
                        variant="table"
                    />
                </div>
            </TableCell>
        </TableRow>
    );
};

export const ItinerariesTable: React.FC<ItinerariesTableProps> = ({
    itineraries,
    currentPage,
    itemsPerPage,
    onPageChange,
    onEdit,
    onDelete,
    onDuplicate,
    onViewTravelRequest,
    onViewItinerary,
    onUpdateStatus,
    onShared,
    isAdmin,
    isTraveler,
    userRole,
}) => {
    const { t } = useI18n();
    const { openModal } = useModal();

    const totalPages = Math.ceil(itineraries.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedItineraries = itineraries.slice(startIndex, startIndex + itemsPerPage);

    const handleRowClick = (itinerary: DashboardItinerary) => {
        openModal('itinerary-details', {
            itinerary,
            onViewItinerary: (id: string) => onViewItinerary?.(id),
        });
    };

    return (
        <div className="space-y-4">
            <div className="overflow-x-auto">
                <div className="min-w-[900px]">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t('itinerary.title')}</TableHead>
                                <TableHead>{t('itinerary.startDate')}</TableHead>
                                <TableHead>{t('itinerary.guestsTitle')}</TableHead>
                                <TableHead>{t('itinerary.status')}</TableHead>
                                <TableHead>{t('travelRequest.contacts')}</TableHead>
                                {isAdmin && <TableHead>{t('travelRequest.designer')}</TableHead>}
                                <TableHead>{t('common.actions')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {paginatedItineraries.map((itinerary) => (
                                <ItineraryTableRow
                                    key={itinerary.id}
                                    itinerary={itinerary}
                                    isAdmin={isAdmin}
                                    isTraveler={isTraveler}
                                    userRole={userRole}
                                    onEdit={onEdit}
                                    onDelete={onDelete}
                                    onDuplicate={onDuplicate}
                                    onViewTravelRequest={onViewTravelRequest}
                                    onViewItinerary={onViewItinerary}
                                    onUpdateStatus={onUpdateStatus}
                                    onShared={onShared}
                                    onRowClick={handleRowClick}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>

            {/* Paginatore */}
            {totalPages > 1 && (
                <div className="flex justify-between items-center">
                    <div className="text-gray-600">
                        {t('dashboard.showingResults', {
                            from: startIndex + 1,
                            to: Math.min(startIndex + itemsPerPage, itineraries.length),
                            total: itineraries.length,
                        })}
                    </div>
                    <Pagination>
                        <PaginationContent>
                            <PaginationItem>
                                <PaginationPrevious
                                    onClick={() => onPageChange(currentPage - 1)}
                                    className={cn("cursor-pointer", currentPage === 1 && "pointer-events-none opacity-50")}
                                />
                            </PaginationItem>

                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                                if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                                    return (
                                        <PaginationItem key={page}>
                                            <PaginationLink
                                                onClick={() => onPageChange(page)}
                                                isActive={currentPage === page}
                                                className="cursor-pointer"
                                            >
                                                {page}
                                            </PaginationLink>
                                        </PaginationItem>
                                    );
                                } else if (page === currentPage - 2 || page === currentPage + 2) {
                                    return (
                                        <PaginationItem key={page}>
                                            <PaginationEllipsis />
                                        </PaginationItem>
                                    );
                                }
                                return null;
                            })}

                            <PaginationItem>
                                <PaginationNext
                                    onClick={() => onPageChange(currentPage + 1)}
                                    className={cn("cursor-pointer", currentPage === totalPages && "pointer-events-none opacity-50")}
                                />
                            </PaginationItem>
                        </PaginationContent>
                    </Pagination>
                </div>
            )}
        </div>
    );
};
